import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomSlider, type SliderRange } from '@/core/components/CustomSlider/CustomSlider';
import { dispatchFilterEvent } from '../../GlobalFilterModal';
import {
  INTERACTIONS_MAX,
  INTERACTIONS_MIN,
  isInteractionRangeFiltered,
  type InteractionsData,
} from './InteractionsContent.defaults';
import './InteractionsContent.scss';

export type { InteractionsData } from './InteractionsContent.defaults';

interface InteractionsContentProps {
  values: InteractionsData;
  onChange: (newValues: InteractionsData) => void;
  onCountChange?: (count: number) => void;
}

export const InteractionsContent: React.FC<InteractionsContentProps> = ({
  values,
  onChange,
  onCountChange,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!onCountChange) return;
    const count =
      Number(isInteractionRangeFiltered(values.likes)) +
      Number(isInteractionRangeFiltered(values.shares)) +
      Number(isInteractionRangeFiltered(values.views)) +
      Number(isInteractionRangeFiltered(values.comments));
    onCountChange(count);
  }, [values, onCountChange]);

  const handleSliderChange = (key: keyof InteractionsData, nextRange: SliderRange) => {
    onChange({ ...values, [key]: nextRange });
    dispatchFilterEvent('INTERACTIONS', key.toUpperCase(), nextRange);
  };

  const sliderConfigs: Array<{ key: keyof InteractionsData; label: string }> = [
    { key: 'likes', label: t('globalFilter.likes') },
    { key: 'shares', label: t('globalFilter.shares') },
    { key: 'views', label: t('globalFilter.views') },
    { key: 'comments', label: t('globalFilter.comments') },
  ];

  const customMarkers = useMemo(() => [
    { value: INTERACTIONS_MIN, label: '0' },
    { value: INTERACTIONS_MAX, label: t('globalFilter.unlimited') },
  ], [t]);

  return (
    <div className="interactions-content">
      {sliderConfigs.map((config) => (
        <div key={config.key} className="interactions-content__slider-group">
          <span className="interactions-content__label">{config.label}</span>

          <div className="interactions-content__slider-wrapper">
            <CustomSlider
              min={INTERACTIONS_MIN}
              max={INTERACTIONS_MAX}
              value={values[config.key]}
              onChange={(range) => handleSliderChange(config.key, range)}
              markers={customMarkers}
            />
          </div>
        </div>
      ))}
    </div>
  );
};